import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";

const TicketsForm = () => {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");

  // Role Logic
  const isRequester = roleId === "3"; // Campus Officer
  const isAssignee = roleId === "2035"; // Assignee Role

  // Chat State
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [chatTicketData, setChatTicketData] = useState(null);
  const [chatMessage, setChatMessage] = useState("");
  const [chatConversations, setChatConversations] = useState([]);

  const [ticketsData, setTicketsData] = useState([]);
  
  // Dropdown Data
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Default Form Values
  const defaultValues = {
    id: 0,
    ticketCode: "",
    subject: "",
    description: "",
    departmentId: null,
    categoryId: null,
    priorityId: null,
    statusId: null,
    attachment: null,
    dueDate: "",
    expectedResolutionDate: "",
    internalNotes: "",
    requesterId: isRequester ? userId : 0,
    assigneeId: 0, 
    base64String: "",
    assigneeId: 0, 
    base64String: "",
    attachmentName: "",
    attachmentPath: ""
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
  });

  const selectedTicketCode = watch("ticketCode");
  const watchId = watch("id");

  // Common Settings Fetcher
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const mappedData = response?.commonSettingDetail?.map(item => ({
          value: item.id,
          label: item.value
      })) || [];

      setData(mappedData);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("Department", setDepartments);
    fetchCommonSettings("ticketCategory", setCategories); 
    fetchCommonSettings("ticketPriority", setPriorities);
    fetchCommonSettings("ticketStatus", setStatuses);
  }, []);

  // Fetch Ticket Code for New Ticket
  const fetchTicketCode = async () => {
      try {
          const response = await get('HelpDesk/GetTicketCode', {
              headers: { Authorization: `Bearer ${token}` }
          });
          // Response is string "TC-00001" directly? Or object? 
          // Assuming direct string based on user prompt "response: TC-00001"
          if(response) {
             setValue("ticketCode", response);
          }
      } catch (error) {
          console.error("Error fetching ticket code:", error);
      }
  };

  useEffect(() => {
      // If Creating New (id is 0 or undefined)
      if(!watchId) {
          fetchTicketCode();
          // Set Default Status to 'Open' if available. 
          // We need statuses loaded first. 
          // We can use a separate effect safely or just handle it when statuses load.
          // For now, let's leave it as explicit selection or wait for statuses.
          // Assuming 'Open' is standard, but we rely on IDs.
      }
  }, [watchId]);

  // Set default status when statuses load and we are in create mode
  useEffect(() => {
      if(!watchId && statuses.length > 0) {
          const openStatus = statuses.find(s => s.label === "Open");
          if(openStatus) {
              setValue("statusId", openStatus);
          }
      }
  }, [statuses, watchId]);


  // Fetch Tickets
  const fetchTickets = async () => {
    try {
      let url = `HelpDesk/GetMyTickets?RequesterId=${userId}&OrganizationId=${organizationId}`;
      
      if (isAssignee) {
          url = `HelpDesk/GetAssignedTickets?AssigneeId=${userId}&OrganizationId=${organizationId}`;
      }

      const response = await get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTicketsData(response || []);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const onSubmit = async (data) => {
    const payload = {        
        createdBy: userId,
        updatedBy: userId,
        id: data.id || 0,
        ticketCode: data.ticketCode,
        subject: data.subject,
        description: data.description,
        departmentId: data.departmentId?.value || 0,
        categoryId: data.categoryId?.value || 0,
        priorityId: data.priorityId?.value || 0,
        statusId: data.statusId?.value || 0,
        requesterId: isRequester ? userId : (data.requesterId || userId),
        assigneeId: data.assigneeId || 0,
        attachmentName: data.attachmentName || "",
        organizationId: Number(organizationId),
        centerId: Number(centerId),
        attachmentPath: "",
        dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : null,
        expectedResolutionDate: data.expectedResolutionDate ? new Date(data.expectedResolutionDate).toISOString() : null,
        internalNotes: data.internalNotes,
        base64String: data.base64String || ""
    };

    console.log("Payload:", payload);

    try {
      const response = await post("HelpDesk/CreateTicket", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.isSuccess) {
        toast.success("Ticket saved successfully!");
        reset(defaultValues);
        fetchTicketCode();
        fetchTickets();
      } else {
        toast.error(response.message || "Failed to save ticket.");
      }
    } catch (error) {
      console.error("Error saving ticket:", error);
    }
  };

  const handleEdit = async (row) => {
      // GET /api/HelpDesk/GetTicketDetail?ticketId&OrganizationId
      try {
          const response = await get(`HelpDesk/GetTicketDetail?ticketId=${row.id}&OrganizationId=${organizationId}`, {
               headers: { Authorization: `Bearer ${token}` }
          });
          
          if(response) {
              const data = response;
              
               // Helper to find label
              const findOpt = (opts, val) => opts.find(o => o.value === val);
              
              const deptOpt = findOpt(departments, data.departmentId);
              const catOpt = findOpt(categories, data.categoryId);
              const prioOpt = findOpt(priorities, data.priorityId);
              const statOpt = findOpt(statuses, data.statusId);

              // Date formatting for input type="date" (YYYY-MM-DD)
              const formatForInput = (dateStr) => {
                  if(!dateStr) return "";
                  // Check if ISO string, take first 10 chars
                  return dateStr.split('T')[0];
              };

              reset({
                  id: data.id,
                  ticketCode: data.ticketCode,
                  subject: data.subject,
                  description: data.description,
                  internalNotes: data.internalNotes,
                  requesterId: data.requesterId,
                  assigneeId: data.assigneeId,
                  organizationId: data.organizationId,
                  departmentId: deptOpt || (data.departmentId ? { value: data.departmentId, label: data.department?.value || "Selected" } : null),
                  categoryId: catOpt || (data.categoryId ? { value: data.categoryId, label: data.category?.value || "Selected" } : null),
                  priorityId: prioOpt || (data.priorityId ? { value: data.priorityId, label: data.priority?.value || "Selected" } : null),
                  statusId: statOpt || (data.statusId ? { value: data.statusId, label: data.status?.value || "Selected" } : null),
                  dueDate: formatForInput(data.dueDate),
                  expectedResolutionDate: formatForInput(data.expectedResolutionDate),
                  attachmentName: data.attachmentName,
                  attachmentPath: (() => {
                      let path = data.attachmentPath || "";
                      if (path.includes("ExternalImages")) {
                          path = path.split("ExternalImages")[1];
                      }
                      path = path.replace(/\\/g, "/");
                      if (data.attachmentName && !path.includes(data.attachmentName)) {
                           path = `${path}/${data.attachmentName}`;
                      }
                      return path.replace("//", "/");
                  })(),
                  base64String: ""
              });
          }

      } catch (error) {
          console.error("Error fetching details:", error);
      }
  };

  // Chat Functions
  const handleOpenChat = async (row) => {
    setChatTicketData(row);
    await fetchChatConversations(row.id);
    setIsChatModalOpen(true);
  };

  const fetchChatConversations = async (ticketId) => {
      try {
        const response = await get(`HelpDesk/GetTicketConversationByTicketIdAsync?ticketId=${ticketId}&userId=${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setChatConversations(response || []);
      } catch (error) {
        console.error("Error fetching chat conversations:", error);
        setChatConversations([]);
      }
  };

  const handleSendChat = async () => {
      if(!chatMessage || !chatMessage.trim()) {
        toast.warning("Please enter a comment.");
        return;
      }
      
      let toUserId = 0;
      // If current user is the requester, send to assignee. Otherwise send to requester.
      if (Number(userId) === Number(chatTicketData.requesterId)) {
          toUserId = chatTicketData.assigneeId;
      } else {
          toUserId = chatTicketData.requesterId;
      }

      const payload = {
          message: chatMessage,
          fromUserId: Number(userId),
          toUserId: Number(toUserId) || 0
      };

      try {
          const response = await post(`HelpDesk/AddTicketReply?ticketId=${chatTicketData.id}`, payload, {
              headers: { Authorization: `Bearer ${token}` }
          });
          
          if(response.isSuccess) {
              setChatMessage("");
              fetchChatConversations(chatTicketData.id); 
          } else {
             toast.error(response.message || "Failed to send message");
          }
      } catch (error) {
           console.error("Error sending message:", error);
      }
  };

  const closeChatModal = () => {
      setIsChatModalOpen(false);
      setChatTicketData(null);
      setChatMessage("");
      setChatConversations([]);
  };

  // Columns for CustomDatatableGrid
  const columns = [
    {
      header: "Ticket Code",
      accessorKey: "ticketCode",
    },
    {
      header: "Subject",
      accessorKey: "subject",
    },
    {
      header: "Department",
      accessorKey: "departmentName",
    },
    {
      header: "Priority",
      accessorKey: "priorityValue",
    },
    {
      header: "Status",
      accessorKey: "statusValue",
    },
    {
      header: "Created On",
      accessorKey: "createDate",
      Cell: ({ cell }) => cell.getValue() ? cell.getValue().split('T')[0] : '',
    },
    {
      header: "Last Updated",
      accessorKey: "lastUpdated",
      Cell: ({ cell }) => cell.getValue() ? cell.getValue().split('T')[0] : '',
    },
    ...(isAssignee ? [{
        header: "Requester",
        accessorKey: "requesterName", 
    }] : []),
    ...(!isAssignee ? [{
        header: "Assignee",
        accessorKey: "assigneeName",
    }] : []),
    {
      header: "Action",
      accessorKey: "actions",
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEdit(row.original)}
          >
          <i className="fas fa-edit"></i>
        </button>
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleOpenChat(row.original)}
          >
          <i className="fas fa-comments"></i>
        </button>
          </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Helpdesk Ticketing System</h2>
      </div>

      <div className="row g-3">
        {/* Left Side: Form */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              {/* <h5 className="mb-3">{watch("id") ? 'Edit Ticket' : 'Create New Ticket'}</h5> */}
              <form className="row g-3 new-form-style" onSubmit={handleSubmit(onSubmit)}>
                
                {/* Ticket Code */}
                <div className="col-md-4">
                  <label className="form-label form-label-new">Ticket Code</label>
                  <Controller
                    name="ticketCode"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className="form-control"
                        placeholder="Auto Generated"
                        disabled
                      />
                    )}
                  />
                </div>

                {/* Subject */}
                <div className="col-md-8">
                  <label className="form-label form-label-new">
                    Subject <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="subject"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className="form-control"
                        disabled={isAssignee}
                      />
                    )}
                  />
                  {errors.subject && <small className="text-danger">{errors.subject.message}</small>}
                </div>

                {/* Department */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Department <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="departmentId"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={departments}
                        placeholder="Select"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isDisabled={isAssignee}
                      />
                    )}
                  />
                  {errors.departmentId && <small className="text-danger">{errors.departmentId.message}</small>}
                </div>

                {/* Category */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Category <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="categoryId"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={categories}
                        placeholder="Select"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isDisabled={isAssignee}
                      />
                    )}
                  />
                  {errors.categoryId && <small className="text-danger">{errors.categoryId.message}</small>}
                </div>

                {/* Description */}
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Description <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        className="form-control"
                        rows="4"
                        disabled={isAssignee}
                      ></textarea>
                    )}
                  />
                  {errors.description && <small className="text-danger">{errors.description.message}</small>}
                </div>

                {/* Priority */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Priority <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="priorityId"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={priorities}
                        placeholder="Select"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isDisabled={isAssignee}
                      />
                    )}
                  />
                  {errors.priorityId && <small className="text-danger">{errors.priorityId.message}</small>}
                </div>
                
                {/* Dates */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">Due Date</label>
                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        className="form-control"
                        min={new Date().toISOString().split("T")[0]}
                        disabled={isAssignee}
                      />
                    )}
                  />
                </div>

                {/* Attachment */}
                <div className="col-md-12">
                    <label className="form-label form-label-new">Attachment</label>
                    <Controller
                        name="attachment"
                        control={control}
                        render={({ field : { value, onChange, ...field } }) => (
                            <input
                                {...field}
                                type="file"
                                className="form-control"
                                onChange={(event) => {
                                    const file = event.target.files[0];
                                    if(file) {
                                       // Create local preview URL
                                       const blobUrl = URL.createObjectURL(file);
                                       setValue("attachmentPath", blobUrl);

                                       const reader = new FileReader();
                                       reader.onloadend = () => {
                                            const base64String = reader.result.split(',')[1];
                                            setValue("base64String", base64String);
                                            setValue("attachmentName", file.name);
                                       };
                                       reader.readAsDataURL(file);
                                    }
                                    onChange(file);
                                }}
                                disabled={isAssignee}
                            />
                        )}
                    />
                    {watch("attachmentPath") && (
                        <div className="mt-2">
                             <a 
                                href={
                                    watch("attachmentPath")?.startsWith("blob:") 
                                    ? watch("attachmentPath") 
                                    : `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${watch("attachmentPath")}`
                                } 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-decoration-none"
                             >
                                 <i className="fas fa-paperclip me-1"></i>
                                 {watch("attachmentName") || "View Attachment"}
                             </a>
                        </div>
                    )}
                </div>

                {/* Status */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">Status</label>
                  <Controller
                    name="statusId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={statuses}
                        placeholder="Status"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isDisabled={isRequester && !watchId} // Disabled for requester only when CREATING
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label form-label-new">
                     Exp. Resolution
                  </label>
                  <Controller
                    name="expectedResolutionDate"
                    control={control}
                    render={({ field }) => (
                         <input
                        {...field}
                        type="date"
                        className="form-control"
                        min={new Date().toISOString().split("T")[0]}
                        disabled={isRequester}
                      />
                    )}
                  />
                </div>

                {/* Internal Notes */}
                <div className="col-md-12">
                  <label className="form-label form-label-new">Internal Notes</label>
                  <Controller
                    name="internalNotes"
                    control={control}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        className="form-control"
                        rows="2"
                        placeholder="Internal use..."
                      ></textarea>
                    )}
                  />
                </div>

                <div className="col-12 mt-4 text-end">
                   <button 
                     type="button" 
                     className="btn btn-outline-primary me-2 btn-sm" 
                     onClick={() => {
                         reset(defaultValues);
                         fetchTicketCode();
                         const openStatus = statuses.find(s => s.label === "Open");
                         if(openStatus) {
                            setValue("statusId", openStatus);
                         }
                     }}
                   >
                     Clear
                   </button>
                   <button 
                     type="submit" 
                     className="btn btn-primary btn-sm" 
                     disabled={isSubmitting}
                   >
                     {isSubmitting ? "Saving..." : "Save"}
                   </button>
                </div>

              </form>
            </div>
          </div>
        </div>

        {/* Right Side: Grid */}
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={ticketsData}
                loading={false} 
                initialState={{
                    columnPinning: { right: ["actions"] },
                  }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      {isChatModalOpen && (
        <>
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header border-bottom-0 pb-0">
                  <h5 className="modal-title fw-bold">
                      Ticket Conversation <span className="badge bg-info fs-9">{chatTicketData?.ticketCode}</span>
                  </h5>
                  <button type="button" className="btn-close" onClick={closeChatModal}></button>
                </div>
                <div className="modal-body pt-0">
                  <div className="d-flex">
                        <div>
                          Subject: <span className="fw-medium">{chatTicketData?.subject}</span> 
                        </div>
                        <div className="ms-auto">
                          <div className="text-black-50 mb-2">
                          Department: {chatTicketData?.departmentName} | Assignee: {chatTicketData?.assigneeName || "Unassigned"} | Status: {chatTicketData?.statusValue}
                        </div>
                    </div>
                  </div>

                    <div className="row g-3">
                      <div className="col-12">
                        <div className="form-group">
                            <textarea 
                                className="form-control mb-2" 
                                rows="5"
                                placeholder="Write your comment" 
                                value={chatMessage}
                                onChange={(e) => setChatMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendChat();
                                    }
                                }}
                            ></textarea>
                            <div className="d-flex justify-content-end align-items-center">
                                <button className="btn btn-primary w-100" onClick={handleSendChat}>
                                    Submit
                                </button>
                            </div>
                        </div>
                      </div>
                      
                      {chatConversations.length > 0 && (
                          <div className="col-12">
                              <div 
                                className="mb-3 pe-3"
                                style={{ 
                                    maxHeight: "400px", 
                                    overflowY: "auto", 
                                }}
                              >
                                <h5 className="fw-medium position-sticky top-0 bg-white pb-1">Conversation History</h5>
                                {chatConversations.map((msg, index) => {
                                    const isMe = String(msg.fromUserId) === String(userId);
                                    return (
                                        <div key={index} className={`d-flex flex-column mb-2 ${!isMe ? "align-items-end" : "align-items-start"}`}>
                                            <div 
                                                style={{ 
                                                    backgroundColor: isMe ? "#E6F0FF" : "#FFE6E6", 
                                                    borderRadius: "4px", 
                                                    padding: "5px 10px", 
                                                    width: "fit-content",
                                                    maxWidth: "80%",
                                                    marginBottom: "5px",
                                                    fontSize: "14px"
                                                }}
                                            >
                                                {msg.message}
                                            </div>
                                            <div className="text-muted fs-10">
                                                {msg.userName} • {msg.createdAt || ""}
                                            </div>
                                        </div>
                                    );
                                })}
                              </div>
                          </div>
                      )}
                    </div>
                   
                </div>  
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TicketsForm;
